import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Banner,
  BarChart,
  Button,
  Card,
  DataTable,
  Skeleton,
  Stat,
  type ColumnDef,
} from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import {
  formatCompact,
  formatCount,
  formatDay,
  formatLastActive,
  formatPercent,
  formatUsd,
  formatWhen,
} from '../../lib/format.js';
import type { UsageConversationDetailResponse } from '../../lib/types.js';
import { modelName, usageErrorMessage } from './usageLabels.js';
import { useUsageConversationLogic } from './useUsageConversationLogic.js';

type TurnRow = UsageConversationDetailResponse['turnRows'][number] & { number: number };

const DAY_MS = 86_400_000;
/** The chart shows the latest turns; the table pages through all of them. */
const CHART_TURNS = 60;
const TURNS_PER_PAGE = 8;

const tokens = (n: number) => <span className="tabular-nums">{formatCompact(n)}</span>;

const columns: ColumnDef<TurnRow>[] = [
  {
    accessorKey: 'at',
    header: 'When',
    cell: (i) => <span className="whitespace-nowrap">{formatWhen(i.getValue<string>())}</span>,
  },
  {
    accessorKey: 'tools',
    header: 'Tools',
    cell: ({ row }) => (
      <span className="font-mono text-xs">
        {row.original.tools.length === 0
          ? 'none'
          : row.original.tools
              .map((t) => (t.count > 1 ? `${t.toolName} ×${String(t.count)}` : t.toolName))
              .join(', ')}
      </span>
    ),
  },
  {
    accessorKey: 'steps',
    header: 'Steps',
    meta: { align: 'right' },
    cell: (i) => {
      const steps = i.getValue<number | null>();
      return (
        <span className="tabular-nums">{steps === null ? 'Not recorded' : formatCount(steps)}</span>
      );
    },
  },
  {
    accessorKey: 'cacheReadTokens',
    header: 'Cache read',
    meta: { align: 'right' },
    cell: (i) => tokens(i.getValue<number>()),
  },
  {
    accessorKey: 'cacheWriteTokens',
    header: 'Cache write',
    meta: { align: 'right' },
    cell: (i) => tokens(i.getValue<number>()),
  },
  {
    accessorKey: 'outputTokens',
    header: 'Output',
    meta: { align: 'right' },
    cell: (i) => tokens(i.getValue<number>()),
  },
  {
    accessorKey: 'costUsd',
    header: 'Cost',
    meta: { align: 'right' },
    cell: (i) => <span className="tabular-nums text-ink">{formatUsd(i.getValue<number>())}</span>,
  },
];

/** "Claude Opus 5, Opus 5.5, Haiku 4.5": the family named once. */
const modelList = (models: string[]): string =>
  models
    .map(modelName)
    .map((name, i) => (i === 0 ? name : name.replace(/^Claude /, '')))
    .join(', ');

/** "turn 160 and 190", "turns 12, 40 and 77". */
const turnList = (numbers: number[]): string => {
  const parts = numbers.map(String);
  const joined =
    parts.length > 1
      ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`
      : (parts[0] ?? '');
  return `${numbers.length > 1 ? 'turns' : 'turn'} ${joined}`;
};

const Detail = ({ data }: { data: UsageConversationDetailResponse }) => {
  const [rawPage, setPage] = useState(0);
  // Newest first from the API; number turns oldest = 1.
  const rows: TurnRow[] = data.turnRows.map((t, i) => ({ ...t, number: data.turns - i }));
  const oldestFirst = [...rows].reverse();
  const chartRows = oldestFirst.slice(-CHART_TURNS).map((t) => ({
    turn: `Turn ${String(t.number)}`,
    rest: (t.costParts?.freshInputUsd ?? 0) + (t.costParts?.outputUsd ?? 0),
    reads: t.costParts?.cacheReadUsd ?? 0,
    writes: t.costParts?.cacheWriteUsd ?? 0,
  }));
  // Each summary folded history just before the first turn at or after it.
  const foldedBefore = data.summaries
    .map((s) => oldestFirst.find((t) => t.at >= s.at)?.number)
    .filter((n): n is number => n !== undefined);
  const days = Math.max(
    1,
    Math.ceil((Date.parse(data.lastActiveAt) - Date.parse(data.startedAt)) / DAY_MS),
  );
  const pageCount = Math.max(1, Math.ceil(rows.length / TURNS_PER_PAGE));
  const page = Math.min(rawPage, pageCount - 1);
  const shown = rows.slice(page * TURNS_PER_PAGE, (page + 1) * TURNS_PER_PAGE);
  const summarized = data.summaries.length;

  return (
    <>
      <section className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-ink">
          {data.title ?? 'Untitled conversation'}
        </h2>
        <p className="text-sm text-ink-muted">
          {data.userLabel} · started {formatDay(data.startedAt)} · last active{' '}
          {formatLastActive(data.lastActiveAt).replace(/^Today/, 'today')} ·{' '}
          {modelList(data.models)}
        </p>
      </section>

      <section className="grid grid-cols-[repeat(auto-fit,minmax(11.25rem,1fr))] gap-4">
        <Stat
          label="Spend (est.)"
          value={formatUsd(data.costUsd)}
          sub="turns, title and summaries"
        />
        <Stat
          label="Turns"
          value={formatCount(data.turns)}
          sub={`over ${String(days)} ${days === 1 ? 'day' : 'days'}`}
        />
        <Stat
          label="Per turn"
          value={data.costPerTurnUsd === null ? 'None' : formatUsd(data.costPerTurnUsd)}
          sub={
            data.tenantCostPerTurnUsd === null
              ? 'no tenant average for these days'
              : `tenant average ${formatUsd(data.tenantCostPerTurnUsd)}`
          }
        />
        <Stat
          label="From cache"
          value={data.fromCacheShare === null ? 'None' : formatPercent(data.fromCacheShare)}
          sub="of chat input, higher is better"
        />
        <Stat
          label="Summarized"
          value={
            summarized === 0
              ? 'Never'
              : `${String(summarized)} ${summarized === 1 ? 'time' : 'times'}`
          }
          sub="history over 100K tokens"
        />
      </section>

      <Card padding="md">
        <div className="flex min-w-0 flex-col gap-3.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-ink">Cost per turn</h3>
            <span className="text-xs text-ink-subtle">
              {rows.length > CHART_TURNS ? `Latest ${String(CHART_TURNS)} turns` : 'Every turn'}
            </span>
          </div>
          <BarChart
            data={chartRows}
            xKey="turn"
            yKey="writes"
            series={[
              { key: 'rest', label: 'Output and fresh input', fill: 'var(--chart-4)' },
              { key: 'reads', label: 'Cache reads', fill: 'var(--chart-1)' },
              { key: 'writes', label: 'Cache writes', fill: 'var(--chart-2)' },
            ]}
            markers={foldedBefore.map((n) => ({
              x: `Turn ${String(n)}`,
              label: 'History summarized',
            }))}
            height={180}
            hideXAxis
            formatValue={formatUsd}
            ariaLabel="Estimated cost per turn, split by kind of token"
            emptyMessage="No priced turns."
          />
          <p className="text-xs text-ink-subtle">
            {foldedBefore.length > 0
              ? `History was summarized before ${turnList(foldedBefore)}. `
              : ''}
            Tall purple bars are turns that rewrote the cached history. After a summary, writes stay
            small until the history grows again.
          </p>
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap items-baseline justify-between gap-2 p-5">
          <h3 className="text-[1.0625rem] font-semibold text-ink">Turns</h3>
          <span className="text-xs text-ink-subtle">
            Newest first. Message text is never shown here.
          </span>
        </div>
        <div className="relative overflow-x-auto">
          <DataTable columns={columns} data={shown} className="min-w-[47.5rem]" />
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-outline px-4 py-3 text-sm text-ink-muted">
          <span>
            Showing {shown.length} of {formatCount(rows.length)} turns
            {data.truncated ? ' (the newest; older turns are left off)' : ''}
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="secondary"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= pageCount - 1}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
};

export const UsageConversationPage = () => {
  const { conversationId = '' } = useParams<{ conversationId: string }>();
  const { isLoading, data, error } = useUsageConversationLogic(conversationId);
  return (
    <AppShell>
      <div className="relative flex min-w-0 flex-col gap-6">
        <Breadcrumbs trail={[{ label: 'Usage', to: '/usage' }]} />
        {error ? (
          <Banner variant="danger">
            <Banner.Body>
              <Banner.Title>Could not load this conversation’s cost.</Banner.Title>
              <Banner.Description>{usageErrorMessage(error)}</Banner.Description>
            </Banner.Body>
          </Banner>
        ) : isLoading || !data ? (
          <div className="flex flex-col gap-4" role="status" aria-label="Loading conversation cost">
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : (
          <Detail data={data} />
        )}
      </div>
    </AppShell>
  );
};
